import React, { useState, useEffect, useContext } from 'react';
import _ from 'lodash';
import styled from 'styled-components/macro';
import { fontDefaultAlt } from '@mirinae/react-ui';

import { FormattedKoPhrase, FormattedEnPhrase } from '@mirinae/lessons/components/lesson/elements/Phrase';
import Element from '@mirinae/lessons/components/lesson/elements/Element';
import { playTTS } from '@mirinae/lessons/modules/libs/lessonLib';
import PlayerContext from '@mirinae/lessons/modules/contexts/player';
import { os } from '@mirinae/js-utils/utils/userAgent';

import ManHover from '@mirinae/lessons/assets/images/man-hovered.svg';
import ManPlaying from '@mirinae/lessons/assets/images/man-playing.svg';
import ManReady from '@mirinae/lessons/assets/images/man-ready.svg';
import WomanHover from '@mirinae/lessons/assets/images/woman-hovered.svg';
import WomanPlaying from '@mirinae/lessons/assets/images/woman-playing.svg';
import WomanReady from '@mirinae/lessons/assets/images/woman-ready.svg';
import SuccessOverlay from '@mirinae/lessons/components/lesson/elements/shared/SuccessOverlay';
import GTM from '@mirinae/apis/GoogleAnalytics';
import Localization from '@mirinae/shared/il8n';

const PracticeTypingElement = styled(Element)`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: start;
`;

const AnswerStage = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    flex-wrap: wrap;
    margin: ${os.isOthers ? 20 : 38}px 0 0px 0;
`;

const CheckButton = styled.button`
    width: 160px;
    height: 50px;
    flex-shrink: 0;
    border-radius: 30px;
    background: ${props => (props.readyForCheck ? '#A5D6D3' : '#DFDFDF')};
    color: #FFF;
    border: none;
    font-size: 17px;
    font-family: ${fontDefaultAlt};
    font-weight: 700;
`;

const DontKnowButton = styled.button`
    font-family: ${fontDefaultAlt};
    background: none;
    color: #A5D6D3;
    font-size: 14px;
    font-weight: 700;
    margin-top: 15px;
    border: none;
`;

const TargetSentence = styled.div`
    display: flex;
    flex-direction: row;
    cursor: pointer;
    align-items: center;
  
    svg {
        transform: scale(${os.isOthers ? 1 : 1.4});
    }
  `;

const targetSentenceStyle = { koFontWeight: '600', koreanFontSize: '20px', align: 'center' };
const answerSentenceStyle = { koFontWeight: '600', koreanFontSize: '20px', align: 'center' };
const englishHintStyle = { enFontWeight: '400', englishFontSize: '14px', align: 'center' };

const PracticeTyping = ({ element }) => {
    const [audioEl, setAudioEl] = useState(null);
    const [playing, setPlaying] = useState(null);
    const [iconMode, setIconMode] = useState('ready');
    const [iconHover, setIconHover] = useState(false);
    const [badPick, setBadPick] = useState(false);
    const [complete, setComplete] = useState(false);
    const player = useContext(PlayerContext);
    const [readyForCheck, setReadyForCheck] = useState(false);
    const [target, setTarget] = useState(null);

    useEffect(() => {
        console.log('cloning target');
        if (!target)
            setTarget(element && _.cloneDeep(element.fieldsByName.target));
    }, [element]);

    const icons = {
        'Playing': () => (element.fieldsByName.gender.value === 'Female' ? <img src={WomanPlaying} alt="Woman playing" /> : <img src={ManPlaying} alt="Man playing" />),
        'Ready': () => (element.fieldsByName.gender.value === 'Female' ? <img src={WomanReady} alt="Woman ready" /> : <img src={ManReady} alt="Man ready" />),
        'Hover': () => (element.fieldsByName.gender.value === 'Female' ? <img src={WomanHover} alt="Woman hover" /> : <img src={ManHover} alt="Man hover" />),
    };

    const fullPhrase = (field) => _.reduce(field.parts.koreanParts, (prev, curr) => prev + curr.value, '');

    const userTyped = (value, part) => {
        part.typeIn = value;
        if (part.showHint) {
            part.typeIn = '';
            part.showHint = false;
            part.ref.value = '';
        }
        if (part.incorrect) {
            part.incorrect = false;
            part.showHint = false;
            setBadPick(false);
        }
        const ready = target.parts.koreanParts.every(p => p.color !== 'typeIn' || p.typeIn);
        if (readyForCheck !== ready)
            setReadyForCheck(ready);
    };

    const check = () => {
        let allDone = true;
        target.parts.koreanParts.forEach(p => {
            if (p.color === 'typeIn') {
                p.incorrect = p.value.replace(/(^\s+)|((\s|\.)+$)/g, '') !== p.typeIn?.replace(/(^\s+)|((\s|\.)+$)/g, '');
                if (p.incorrect)
                    allDone = false;
                else
                    p.color = 'example';
            }
        });
        if (allDone) {
            player.postEvent({ action: 'complete' }, element);
            GTM.userAction('success', { page: player.currentPage.componentName }, 'lessons');
            setComplete(true);
        } else {
            GTM.userAction('error', { page: player.currentPage.componentName }, 'lessons');
            player.postEvent({ action: 'error' }, element);
            setBadPick(true);
        }
        setTarget(_.cloneDeep(target)); // { ...target });
    };

    const showHints = () => {
        target.parts.koreanParts.forEach(p => {
            if (p.color === 'typeIn' && p.incorrect) {
                p.showHint = 'show';
                p.incorrect = false;
                p.typeIn = '';
            }
        });
        setTarget(_.cloneDeep(target));
    };

    return (
        <PracticeTypingElement element={element}>
            { complete ? <SuccessOverlay top={120} left={1} /> : null }
            <TargetSentence
                onClick={() => playTTS(audioEl, fullPhrase(element.fieldsByName.answer), null, { speed: '80%', speaker: element.fieldsByName.gender.value.toLowerCase() })}
                onMouseEnter={() => setIconHover(true)}
                onMouseLeave={() => (iconHover ? setIconHover(false) : null)}
            >
                { playing ? <icons.Ready /> : iconMode === 'Playing' ? <icons.Playing /> : iconMode === 'Ready' ? iconHover ? <icons.Hover /> : <icons.Ready /> : <icons.Ready /> }
            </TargetSentence>

            <AnswerStage>
                { target && (
                    <FormattedKoPhrase
                        phrase={complete ? element.fieldsByName.answer : target}
                        phraseStyle={complete ? answerSentenceStyle : targetSentenceStyle}
                        onTypeIn={userTyped} typeInIncorrect={badPick}
                    />
                )}
                <div style={{ margin: '5px 0 30px 0' }}>
                    <FormattedEnPhrase phrase={element.fieldsByName.answer} phraseStyle={englishHintStyle} />
                </div>
                { !complete && <CheckButton disabled={!readyForCheck} readyForCheck={readyForCheck} onClick={check}>{Localization['player-check']}</CheckButton> }
                { badPick && <DontKnowButton onClick={showHints}>{Localization['player-i-dont-know']}</DontKnowButton> }
            </AnswerStage>

            <audio
                ref={setAudioEl} 
                onPlay={() => setIconMode('Playing')}
                onEnded={() => { setIconMode('Ready'); setPlaying(null); }}
            />
        </PracticeTypingElement>
    );
};

export default PracticeTyping;
